import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useJsonLd } from '@/hooks/useJsonLd';
import { usePageMeta } from '@/hooks/usePageMeta';

const packages = [
  {
    name: 'Smile Starter',
    price: '99€',
    duration: '30 Min.',
    desc: 'Perfekt für eine schnelle, sichtbare Auffrischung.',
    icon: 'ri-sun-line',
  },
  {
    name: 'Smile Classic',
    price: '149€',
    duration: '45 Min.',
    desc: 'Unser Bestseller — intensivere Aufhellung, mehr Wirkung.',
    icon: 'ri-star-line',
    featured: true,
  },
  {
    name: 'Smile Premium',
    price: '199€',
    duration: '60 Min.',
    desc: 'Maximale Aufhellung inkl. Pflege-Set & Nachsorge.',
    icon: 'ri-vip-crown-line',
  },
];

export default function Termin() {
  const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined)?.replace(/\/$/, '') ?? '';

  usePageMeta({ canonical: '/termin' });

  useJsonLd({
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Kosmetische Zahnaufhellung buchen – Flow & Glow Köln',
    url: `${siteUrl || 'https://flowundglow.de'}/termin`,
    description: 'Jetzt Termin für kosmetische Zahnaufhellung in Köln buchen. Schmerzfrei, peroxidfrei mit PAP-Technologie. Termine ab 99 €.',
    provider: {
      '@type': 'HealthAndBeautyBusiness',
      name: 'Flow & Glow',
      url: siteUrl || 'https://flowundglow.de',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Köln',
        addressCountry: 'DE',
      },
    },
    offers: [
      {
        '@type': 'Offer',
        name: 'Smile Starter',
        price: '99',
        priceCurrency: 'EUR',
        description: 'Perfekt für eine schnelle, sichtbare Auffrischung. 30 Minuten.',
        availability: 'https://schema.org/InStock',
      },
      {
        '@type': 'Offer',
        name: 'Smile Classic',
        price: '149',
        priceCurrency: 'EUR',
        description: 'Unser Bestseller – intensivere Aufhellung, mehr Wirkung. 45 Minuten.',
        availability: 'https://schema.org/InStock',
      },
      {
        '@type': 'Offer',
        name: 'Smile Premium',
        price: '199',
        priceCurrency: 'EUR',
        description: 'Maximale Aufhellung inkl. Pflege-Set & Nachsorge. 60 Minuten.',
        availability: 'https://schema.org/InStock',
      },
    ],
  });

  return (
    <main className="font-sans bg-offwhite min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="bg-charcoal pt-28 pb-14 md:pt-36 md:pb-16">
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
          <span className="inline-block font-sans text-xs tracking-widest uppercase text-rose font-semibold mb-4">
            Jetzt Termin sichern
          </span>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium text-white leading-tight mb-5">
            Buche deinen Termin
          </h1>
          <p className="font-sans text-base md:text-lg text-white/65 leading-relaxed max-w-xl mx-auto">
            Wähle deine Wunschanwendung und einen freien Termin —
            in unter 2 Minuten.
          </p>
        </div>
      </section>

      {/* Booking Embed */}
      <section className="bg-offwhite py-10 md:py-12">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div
            className="w-full rounded-3xl border-2 border-dashed border-rose/30 bg-white flex flex-col items-center justify-center gap-6 text-center px-8"
            style={{ minHeight: '640px' }}
          >
            <div className="w-16 h-16 flex items-center justify-center bg-rose/10 rounded-full">
              <i className="ri-calendar-2-line text-rose text-3xl" />
            </div>
            <div>
              <p className="font-serif text-2xl md:text-3xl font-medium text-charcoal mb-3">
                Buchungstool folgt hier
              </p>
              <p className="font-sans text-base text-charcoal/50 max-w-md mx-auto leading-relaxed">
                Hier wird das Buchungstool eingebettet —
                SimplyBook.me / Calendly
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mt-2">
              <a
                href="https://simplybook.me"
                target="_blank"
                rel="nofollow noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-rose hover:bg-rose-dark text-white font-sans font-medium text-sm px-7 py-3.5 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-external-link-line text-sm" />
                </span>
                Direkt bei SimplyBook.me buchen
              </a>
              <a
                href="https://wa.me/4922100000"
                rel="nofollow noreferrer"
                className="inline-flex items-center justify-center gap-2 border border-charcoal text-charcoal hover:bg-charcoal/5 font-sans font-medium text-sm px-7 py-3.5 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-whatsapp-line text-sm" />
                </span>
                Termin per WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Package Info Cards */}
      <section className="bg-offwhite pt-2 pb-14 md:pb-20">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <p className="font-sans text-xs uppercase tracking-widest font-semibold text-charcoal/40 text-center mb-6">
            Verfügbare Anwendungen
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {packages.map((pkg) => (
              <div
                key={pkg.name}
                className={`relative rounded-2xl px-6 py-7 flex flex-col gap-3 transition-all duration-200 ${
                  pkg.featured
                    ? 'bg-charcoal ring-2 ring-rose/60'
                    : 'bg-white ring-1 ring-blush'
                }`}
              >
                {pkg.featured && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap inline-flex items-center gap-1 bg-rose text-white font-sans text-[10px] font-semibold tracking-wide px-3 py-1 rounded-full">
                    <i className="ri-star-fill text-[9px]" />
                    Beliebteste Wahl
                  </span>
                )}

                {/* Icon */}
                <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${pkg.featured ? 'bg-rose/20' : 'bg-rose/10'}`}>
                  <i className={`${pkg.icon} text-rose text-base`} />
                </div>

                {/* Name + price row */}
                <div className="flex items-end justify-between gap-2">
                  <h3 className={`font-sans font-semibold text-base leading-tight ${pkg.featured ? 'text-white' : 'text-charcoal'}`}>
                    {pkg.name}
                  </h3>
                  <span className={`font-serif text-2xl font-medium leading-none ${pkg.featured ? 'text-white' : 'text-charcoal'}`}>
                    {pkg.price}
                  </span>
                </div>

                {/* Duration */}
                <div className="flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-time-line text-rose text-sm" />
                  </span>
                  <span className={`font-sans text-sm ${pkg.featured ? 'text-white/55' : 'text-charcoal/50'}`}>
                    {pkg.duration}
                  </span>
                </div>

                {/* Description */}
                <p className={`font-sans text-sm leading-relaxed ${pkg.featured ? 'text-white/60' : 'text-charcoal/55'}`}>
                  {pkg.desc}
                </p>
              </div>
            ))}
          </div>

          {/* Details link */}
          <p className="text-center mt-6">
            <Link
              to="/leistungen"
              className="font-sans text-sm text-charcoal/50 hover:text-rose transition-colors duration-200 cursor-pointer inline-flex items-center gap-1.5"
            >
              Alle Leistungen im Detail ansehen
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm" />
              </span>
            </Link>
          </p>
        </div>
      </section>

      {/* Anfahrt & Standort */}
      <section className="bg-white py-14 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <span className="inline-block font-sans text-xs tracking-widest uppercase text-rose font-semibold mb-3">
              Wo du uns findest
            </span>
            <h2 className="font-serif text-3xl md:text-4xl font-medium text-charcoal leading-tight">
              Anfahrt & Standort
            </h2>
            <p className="font-sans text-sm text-charcoal/55 mt-3 max-w-md mx-auto leading-relaxed">
              Flow &amp; Glow befindet sich zentral in Köln — gut erreichbar mit
              öffentlichen Verkehrsmitteln und PKW.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
            {/* Info Cards */}
            <div className="flex flex-col gap-4">
              {/* Adresse */}
              <div className="bg-offwhite rounded-2xl p-6 flex gap-4 items-start ring-1 ring-blush">
                <div className="w-10 h-10 flex items-center justify-center bg-rose/10 rounded-xl flex-shrink-0">
                  <i className="ri-map-pin-2-line text-rose text-lg" />
                </div>
                <div>
                  <p className="font-sans font-semibold text-sm text-charcoal mb-1">Adresse</p>
                  <p className="font-sans text-sm text-charcoal/60 leading-relaxed">
                    Flow &amp; Glow<br />
                    Köln, Deutschland<br />
                    <span className="text-charcoal/35 text-xs italic">Genaue Adresse folgt</span>
                  </p>
                </div>
              </div>

              {/* Öffnungszeiten */}
              <div className="bg-offwhite rounded-2xl p-6 flex gap-4 items-start ring-1 ring-blush">
                <div className="w-10 h-10 flex items-center justify-center bg-rose/10 rounded-xl flex-shrink-0">
                  <i className="ri-time-line text-rose text-lg" />
                </div>
                <div>
                  <p className="font-sans font-semibold text-sm text-charcoal mb-1">Öffnungszeiten</p>
                  <div className="font-sans text-sm text-charcoal/60 leading-relaxed space-y-0.5">
                    <p>Mo – Fr: 10:00 – 19:00 Uhr</p>
                    <p>Sa: 10:00 – 16:00 Uhr</p>
                    <p className="text-charcoal/35 text-xs mt-1">Nur nach Termin</p>
                  </div>
                </div>
              </div>

              {/* Erreichbarkeit */}
              <div className="bg-offwhite rounded-2xl p-6 flex gap-4 items-start ring-1 ring-blush">
                <div className="w-10 h-10 flex items-center justify-center bg-rose/10 rounded-xl flex-shrink-0">
                  <i className="ri-train-line text-rose text-lg" />
                </div>
                <div>
                  <p className="font-sans font-semibold text-sm text-charcoal mb-1">Anreise</p>
                  <div className="font-sans text-sm text-charcoal/60 leading-relaxed space-y-0.5">
                    <p>Gut erreichbar per KVB</p>
                    <p>Parkplätze in der Nähe</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Google Maps Embed */}
            <div className="lg:col-span-2 rounded-2xl overflow-hidden ring-1 ring-blush" style={{ minHeight: '380px' }}>
              <iframe
                title="Flow & Glow Standort Köln"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d80434.72375789786!2d6.887213!3d50.938361!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47bf25aa4e37d2cb%3A0xf50db4c5ffe2f0ac!2sK%C3%B6ln!5e0!3m2!1sde!2sde!4v1711900000000!5m2!1sde!2sde"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: '380px' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>

      {/* WhatsApp CTA */}
      <section className="bg-blush py-14 md:py-16">
        <div className="max-w-xl mx-auto px-4 md:px-8 text-center">
          <div className="w-12 h-12 flex items-center justify-center bg-rose/15 rounded-full mx-auto mb-5">
            <i className="ri-whatsapp-line text-rose text-2xl" />
          </div>
          <h2 className="font-serif text-2xl md:text-3xl font-medium text-charcoal mb-3 leading-tight">
            Fragen? Schreib Jenny direkt per WhatsApp.
          </h2>
          <p className="font-sans text-sm text-charcoal/60 leading-relaxed mb-7">
            Unsicher welche Anwendung zu dir passt? Jenny antwortet persönlich — meistens noch am selben Tag.
          </p>
          <a
            href="https://wa.me/4922100000"
            rel="nofollow noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-rose hover:bg-rose-dark text-white font-sans font-medium text-base px-8 py-4 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-whatsapp-line text-base" />
            </span>
            WhatsApp schreiben
          </a>
          <p className="font-sans text-xs text-charcoal/40 mt-4">
            Oder per E-Mail:&nbsp;
            <a href="mailto:hallo@flowundglow.de" className="underline hover:text-rose transition-colors duration-200 cursor-pointer">
              hallo@flowundglow.de
            </a>
          </p>
        </div>
      </section>

      <Footer />
    </main>
  );
}
